import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import validate from 'utils/validate';
import { cloneDeep, trim } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { listInsuranceProviders } from '../../../repositories/HttpRepository';
import GenericForm from '../../../components/GenericForm';
import { usePrevious } from '../../../utils/hooks/usePrevious';

const useStyles = makeStyles()((theme) => ({
  dialogAction: {
    padding: '8px 24px',
  },
  title: {
    fontWeight: 500,
    fontSize: '1.2rem',
    textTransform: 'uppercase',
  },
  btn: {
    boxShadow: 'none',
    textTransform: 'inherit',
    width: '100px',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  error: {
    fontWeight: 500,
    fontSize: '14px',
  },
}));

function isEmpty(value) {
  return value === undefined || value === null || value.length === 0;
}

//custom validators
validate.validators.validateAddress = (value, options, key, attributes) => {
  if (key === 'zipcode' && !!value) {
    if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value)) {
      return 'is not valid';
    }
  }

  if (attributes['insuranceEnabled']) {
    switch (key) {
      case 'street':
      case 'city':
      case 'state':
      case 'zipcode':
        if (!value) {
          return 'is required when Opt-in for Insurance is enabled';
        }
        break;
    }
  } else if (!isEmpty(attributes['street']) || !isEmpty(attributes['street2']) || !isEmpty(attributes['city']) || !isEmpty(attributes['state']) || !isEmpty(attributes['zipcode'])) {
    if ((key === 'street' || key === 'city' || key === 'state' || key === 'zipcode') && isEmpty(value)) {
      return 'is required when other address fields are filled in';
    }
  }
  return null;
};

validate.validators.validateInsuranceProvider = (value, options, key, attributes) => {
  if (key === 'insuranceProvider' && attributes['insuranceEnabled'] && !value) {
    return 'is required when Opt-in for Insurance is enabled';
  }
  return null;
};

validate.validators.validateSupportEmail = (value, options, key, attributes) => {
  if (value !== undefined && value !== null && value !== '') {
    return validate.single(value, { email: true });
  }
  return null;
};

const CLIENT_INFO_FORM_VALIDATE_RULES = {
  username: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
  },
  password: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
  },
  company: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
  },
  name: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
  },
  email: {
    email: {
      message: 'is not a valid email',
    },
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
  },
  support_email: {
    validateSupportEmail: true,
  },
  phone_number: {
    format: {
      pattern: /\d{3}-\d{3}-\d{4}$/,
      message: 'is wrong phone number format',
    },
  },
  code: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
    length: {
      minimum: 4,
      maximum: 7,
    },
  },
  street: {
    validateAddress: true,
  },
  street2: {
    validateAddress: true,
  },
  city: {
    validateAddress: true,
  },
  state: {
    validateAddress: true,
  },
  zipcode: {
    validateAddress: true,
  },
  timezone: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
  },
  insuranceProvider: {
    validateInsuranceProvider: true,
  },
};

const validateClientInfoForm = (client, isEditMode) => {
  let params = cloneDeep(client);
  for (const key in params) {
    if (typeof params[key] === 'string') params[key] = trim(params[key]);
  }
  const rules = CLIENT_INFO_FORM_VALIDATE_RULES;
  if (isEditMode) {
    delete rules.code;
    delete rules.username;
    delete rules.password;
  }

  const errors = validate(params, CLIENT_INFO_FORM_VALIDATE_RULES);
  if (errors) {
    const messages = {};
    for (const key in errors) {
      messages[key] = [errors[key][0]];
    }
    return errors;
  }
};

const ClientInfoForm = forwardRef((props, ref) => {
  const { classes } = useStyles();
  const { initialData, onClose, error, setError, handleDataChange, loading } = props;
  const previousData = usePrevious(initialData);
  const [client, setClient] = useState(initialData);
  const [formErrors, setFormErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(props.isEditMode || false);
  const [providers, setProviders] = useState([]);

  const initialFormFields = [
    {
      id: 'username',
      label: 'User Name',
      variant: 'outlined',
      autoComplete: 'off',
      column: 12,
      autoFocus: true,
      hidden: isEditMode,
    },
    {
      id: 'password',
      label: 'Password',
      variant: 'outlined',
      autoComplete: 'off',
      column: 12,
      type: 'password',
      hidden: isEditMode,
    },
    {
      id: 'company',
      label: 'Company Name',
      variant: 'outlined',
      autoComplete: 'off',
      column: !isEditMode ? 12 : 6,
    },
    {
      id: 'name',
      label: 'Contact Name',
      variant: 'outlined',
      autoComplete: 'off',
      column: !isEditMode ? 12 : 6,
    },
    {
      id: 'email',
      label: 'Email',
      variant: 'outlined',
      autoComplete: 'off',
      column: !isEditMode ? 12 : 6,
      type: 'email',
    },
    {
      id: 'support_email',
      label: 'Support Email',
      variant: 'outlined',
      autoComplete: 'off',
      column: !isEditMode ? 12 : 6,
      type: 'email',
      notRequired: true,
    },
    {
      id: 'phone_number',
      label: 'Phone Number',
      variant: 'outlined',
      autoComplete: 'off',
      column: !isEditMode ? 12 : 6,
      type: 'phone',
    },
    isEditMode && {
      id: 'timezone',
      label: 'Timezone',
      variant: 'outlined',
      column: !isEditMode ? 12 : 6,
      type: 'timeZoneSelect',
    },
    {
      id: 'code',
      label: 'Code',
      variant: 'outlined',
      autoComplete: 'off',
      hidden: isEditMode,
    },
    {
      id: 'street',
      label: 'Address Line 1',
      subLabel: '(house or building number and street name)',
      variant: 'outlined',
      autoComplete: 'off',
      column: 12,
      notRequired: true,
    },
    {
      id: 'street2',
      label: 'Address Line 2',
      subLabel: '(unit or apartment information)',
      variant: 'outlined',
      autoComplete: 'off',
      column: 12,
      notRequired: true,
    },
    {
      id: 'city',
      label: 'City',
      variant: 'outlined',
      autoComplete: 'off',
      column: 4,
      notRequired: true,
    },
    {
      id: 'state',
      label: 'State',
      variant: 'outlined',
      autoComplete: 'off',
      column: 4,
      notRequired: true,
      type: 'stateSelect',
    },
    {
      id: 'zipcode',
      label: 'Zipcode',
      variant: 'outlined',
      autoComplete: 'off',
      column: 4,
      notRequired: true,
      type: 'zipcode',
    },
    !isEditMode && {
      id: 'timezone',
      label: 'Timezone',
      variant: 'outlined',
      column: 12,
      type: 'timeZoneSelect',
    },
    {
      id: 'insuranceEnabled',
      label: 'Opt-in for Insurance',
      subLabel: '(address is mandatory when opted-in)',
      variant: 'outlined',
      type: 'checkbox',
      column: 12,
      labelPlacement: 'end',
    },
    {
      id: 'insuranceProvider',
      label: 'Insurance Provider',
      variant: 'outlined',
      type: 'select',
      column: 12,
      selectOptions: [],
      hidden: true,
    },
  ].filter(Boolean);

  useEffect(() => {
    if (props.isEditMode) {
      setIsEditMode(true);
    }
  }, [props.isEditMode]);

  useImperativeHandle(ref, () => ({
    validateForm() {
      return validateData();
    },
  }));

  useEffect(() => {
    if (initialData !== previousData) {
      setClient(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchInsuranceProviders = async () => {
      const response = await listInsuranceProviders();
      if (response.data) {
        const insuranceProviders = await response.data.map((provider) => ({
          key: provider,
          value: provider,
          label: provider.toUpperCase(),
        }));
        setProviders(insuranceProviders);
      }
    };
    fetchInsuranceProviders().catch(console.error);
  }, []);

  const handleChange = (name, value) => {
    let clonedClient = cloneDeep(client);
    let clonedFormErrors = cloneDeep(formErrors);
    clonedClient[name] = value;
    if (name === 'insuranceEnabled') {
      clonedFormErrors = {
        ...clonedFormErrors,
        street: '',
        street2: '',
        city: '',
        state: '',
        zipcode: '',
      };
      if (!value) {
        clonedClient['insuranceProvider'] = '';
        //use current set insurance provider from original data if it exists
      } else if (isEditMode) {
        if (client?.insuranceProvider === '' && client.insuranceProvider !== previousData.insuranceProvider) {
          clonedClient['insuranceProvider'] = previousData.insuranceProvider;
        }
      }
    } else if (name in CLIENT_INFO_FORM_VALIDATE_RULES) {
      const newErrors = validate(clonedClient, {
        [name]: CLIENT_INFO_FORM_VALIDATE_RULES[name],
      });
      if (newErrors) {
        const messages = {};
        for (const key in newErrors) {
          messages[key] = [newErrors[key][0]];
        }
        clonedFormErrors = { ...clonedFormErrors, ...messages };
      } else {
        clonedFormErrors = { ...clonedFormErrors, [name]: '' };
      }
      if (
        (!('insuranceEnabled' in clonedClient) || !clonedClient['insuranceEnabled']) &&
        isEmpty(clonedClient['street']) &&
        isEmpty(clonedClient['street2']) &&
        isEmpty(clonedClient['city']) &&
        isEmpty(clonedClient['state']) &&
        isEmpty(clonedClient['zipcode'])
      ) {
        clonedFormErrors = {
          ...clonedFormErrors,
          street: '',
          street2: '',
          city: '',
          state: '',
          zipcode: '',
        };
      }
    } else {
      clonedFormErrors = { ...clonedFormErrors, [name]: '' };
    }

    setClient(clonedClient);
    setError(null);
    setFormErrors(clonedFormErrors);
  };

  useEffect(() => {
    if (handleDataChange) {
      if (previousData !== client) {
        handleDataChange(client);
      }
    }
  }, [client]);

  function useTraceUpdate(props) {
    const prev = useRef(props);
    useEffect(() => {
      const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
        if (prev.current[k] !== v) {
          ps[k] = [prev.current[k], v];
        }
        return ps;
      }, {});
      if (Object.keys(changedProps).length > 0) {
        // console.log('Changed props:', changedProps);
      }
      prev.current = props;
    });
  }

  const clearAddressFormErrors = () => {
    setFormErrors({
      ...formErrors,
      street: '',
      street2: '',
      city: '',
      state: '',
      zipcode: '',
    });
  };

  const validateData = () => {
    let params = cloneDeep(client);
    const errors = validateClientInfoForm(params, isEditMode);
    if (errors) {
      const messages = {};
      for (const key in errors) {
        messages[key] = [errors[key][0]];
      }
      setFormErrors(messages);
      return errors;
    } else {
      setFormErrors({});
    }
  };

  const handleOnBlur = (e) => {
    const { name } = e.target;

    if (name in CLIENT_INFO_FORM_VALIDATE_RULES) {
      let params = cloneDeep(client);
      for (const key in params) {
        if (typeof params[key] === 'string') params[key] = trim(params[key]);
      }

      const newErrors = validate(params, {
        [name]: CLIENT_INFO_FORM_VALIDATE_RULES[name],
      });
      if (newErrors) {
        const messages = {};
        for (const key in newErrors) {
          messages[key] = [newErrors[key][0]];
        }
        setFormErrors((prevState) => {
          return { ...prevState, ...messages };
        });
        return;
      }

      if (!('insuranceEnabled' in params) && isEmpty(params['street']) && isEmpty(params['city']) && isEmpty(params['state']) && isEmpty(params['zipcode'])) {
        clearAddressFormErrors();
      }
    }
  };

  const formatFormErrors = (formErrors) => {
    if (formErrors) {
      if ('street' in formErrors) {
        formErrors['street'] = formErrors['street'][0]?.replace('Street ', 'Address Line 1 ');
      }
    }
    return formErrors;
  };

  let formFields = cloneDeep(initialFormFields);

  formFields.forEach((field) => {
    if (field.id === 'insuranceProvider') {
      field.hidden = !client.insuranceEnabled;
    }
  });
  formFields.forEach((field) => {
    if (field.id === 'insuranceProvider') {
      field.selectOptions = providers;
    }
  });

  const formattedFormErrors = formatFormErrors(cloneDeep(formErrors));

  return (
    <GenericForm
      data={client}
      error={error}
      formErrors={formattedFormErrors}
      formFields={formFields}
      onClose={onClose}
      onCancel={onClose}
      handleChange={handleChange}
      classes={classes}
      onBlur={handleOnBlur}
    />
  );
});

export default React.memo(ClientInfoForm);
